import { FormEvent, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ReCAPTCHA from 'react-google-recaptcha';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import api from '../../../api/axiosInstance';
import { showError, showSuccess } from '../../../components/notifications';
import { RECAPTCHA_SITE_KEY } from '../../../constants';
import { emailRegex } from '../../../utils/regex';
import { Loader } from '../../../components/loader';

interface RegisterResponse {
  message: string;
}

const PASSWORD_MIN = 6;

export default function Register() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const [touched, setTouched] = useState({ email: false, password: false, confirm: false });
  const [isFormValid, setIsFormValid] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState<boolean>(true);

  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const navigate = useNavigate();

  const isEmailValid = emailRegex.test(email);
  const isPasswordValid = password.length >= PASSWORD_MIN;
  const isConfirmValid = password === confirmPassword;

  useEffect(() => {
    setIsFormValid(Boolean(isEmailValid && isPasswordValid && isConfirmValid && recaptchaToken));
  }, [isEmailValid, isPasswordValid, recaptchaToken, isConfirmValid]);

  const { mutate, isPending } = useMutation<RegisterResponse, Error, void>({
    mutationFn: () =>
      api
        .post<RegisterResponse>('/auth/register', {
          email,
          password,
          confirmPassword,
          recaptchaToken,
          marketingConsent,
        })
        .then(res => res.data),

    onSuccess: data => {
      showSuccess(data.message);
      navigate('/confirmemail');
    },

    onError: err => {
      const msg = err instanceof Error ? err.message : 'Ошибка при регистрации';
      showError(msg);
      recaptchaRef.current?.reset();
      setRecaptchaToken(null);
    },
  });

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setTouched({ email: true, password: true, confirm: true });
    if (!isFormValid) return;
    mutate();
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Stack spacing={2}>
        <TextField
          label="Email"
          variant="outlined"
          fullWidth
          value={email}
          onChange={e => setEmail(e.target.value)}
          onBlur={() => setTouched(t => ({ ...t, email: true }))}
          error={touched.email && !isEmailValid}
          InputLabelProps={{ shrink: true }}
          helperText={touched.email && !isEmailValid ? 'Введите корректный email' : ' '}
          disabled={isPending}
          autoComplete="email"
        />

        <TextField
          label="Пароль"
          type={showPassword ? 'text' : 'password'}
          variant="outlined"
          fullWidth
          value={password}
          onChange={e => setPassword(e.target.value)}
          onBlur={() => setTouched(t => ({ ...t, password: true }))}
          error={touched.password && !isPasswordValid}
          InputLabelProps={{ shrink: true }}
          helperText={touched.password && !isPasswordValid ? `Минимум ${PASSWORD_MIN} символов` : ' '}
          disabled={isPending}
          autoComplete="new-password"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                  onClick={() => setShowPassword(v => !v)}
                  edge="end"
                  disabled={isPending}
                >
                  {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <TextField
          label="Подтвердите пароль"
          type={showConfirmPassword ? 'text' : 'password'}
          variant="outlined"
          fullWidth
          value={confirmPassword}
          InputLabelProps={{ shrink: true }}
          onChange={e => setConfirmPassword(e.target.value)}
          onBlur={() => setTouched(t => ({ ...t, confirm: true }))}
          error={touched.confirm && !isConfirmValid}
          helperText={touched.confirm && !isConfirmValid ? 'Пароли не совпадают' : ' '}
          disabled={isPending}
          autoComplete="new-password"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label={showConfirmPassword ? 'Скрыть пароль' : 'Показать пароль'}
                  onClick={() => setShowConfirmPassword(v => !v)}
                  edge="end"
                  disabled={isPending}
                >
                  {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={marketingConsent}
              onChange={e => setMarketingConsent(e.target.checked)}
              disabled={isPending}
            />
          }
          label="Я согласен получать информационные письма"
        />

        <Box sx={{ overflowX: 'auto' }}>
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={RECAPTCHA_SITE_KEY}
            onChange={token => setRecaptchaToken(token)}
          />
        </Box>

        <Typography variant="caption" color="text.secondary">
          Регистрируясь, вы принимаете{' '}
          <Link href="/privacy-policy" target="_blank" rel="noreferrer">
            политику персональных данных
          </Link>{' '}
          и{' '}
          <Link href="/cookie-policy" target="_blank" rel="noreferrer">
            политику cookie
          </Link>
          .
        </Typography>

        <Button type="submit" variant="contained" fullWidth disabled={!isFormValid || isPending} size="large">
          {isPending ? <Loader /> : 'Зарегистрироваться'}
        </Button>
      </Stack>
    </Box>
  );
}
